'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import { Link, useNavigate, useLocation } from '../../utils/router';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import './Header.css';

const ThemeToggle = dynamic(() => import('../common/ThemeToggle'), {
  ssr: false,
  loading: () => null,
});

const NotificationBell = dynamic(() => import('./NotificationBell'), {
  ssr: false,
  loading: () => null,
});

const Header = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const msgIntervalRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const notifPermissionRef = useRef('default');

  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      notifPermissionRef.current = Notification.permission;
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      notifPermissionRef.current = result;
    }
  }, []);

  const sendNotification = useCallback((newCount) => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible' && document.hasFocus()) return;

    const notif = new Notification('PuiPi - 新訊息', {
      body: `你有 ${newCount} 則未讀訊息`,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'puipi-unread',
      renotify: true,
    });
    notif.onclick = () => {
      window.focus();
      notif.close();
    };
  }, []);

  const fetchUnreadMessages = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/messages/unread-count');
      const count = data.count || 0;
      if (count > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        sendNotification(count);
      }
      prevUnreadRef.current = count;
      setUnreadMessages(count);
    } catch {
      // ignore
    }
  }, [user, sendNotification]);

  useEffect(() => {
    if (!user) { setUnreadMessages(0); prevUnreadRef.current = 0; return; }
    requestNotificationPermission();
    fetchUnreadMessages();
    msgIntervalRef.current = setInterval(fetchUnreadMessages, 5000);
    return () => clearInterval(msgIntervalRef.current);
  }, [user, fetchUnreadMessages, requestNotificationPermission]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  return (
    <header className="header">
      <div className="container header-content">
        <Link to="/" className="logo">
          <span className="logo-icon" aria-hidden="true">🐾</span>
          <span className="logo-text">PuiPi</span>
        </Link>

        <button
          className="menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
        >
          <span className={`hamburger ${menuOpen ? 'open' : ''}`}></span>
        </button>

        {menuOpen && <div className="nav-overlay" onClick={() => setMenuOpen(false)} />}
        <nav id="mobile-nav" className={`nav ${menuOpen ? 'nav-open' : ''}`}>
          {[
            { to: '/', label: t('nav.home') },
            { to: '/sell', label: t('nav.sell') },
            { to: '/wanted', label: t('nav.wanted') },
            { to: '/favorites', label: t('nav.favorites') },
            { to: '/orders', label: t('nav.orders') },
            { to: '/dog-walking', label: t('nav.dogWalking') },
            { to: '/adoption', label: t('nav.adoption') },
          ].map(({ to, label }) => {
            const fullPath = `/zh-tw${to === '/' ? '' : to}`;
            const isActive = to === '/' ? location.pathname === '/zh-tw/' || location.pathname === '/zh-tw' : location.pathname.startsWith(fullPath);
            return (
              <Link
                key={to}
                to={to}
                className={`nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
                {...(isActive ? { 'aria-current': 'page' } : {})}
              >
                {label}
              </Link>
            );
          })}
        </nav>

        <div className={`header-actions ${menuOpen ? 'actions-open' : ''}`}>
          <ThemeToggle />
          {user && <NotificationBell />}
          {user && (
            <Link to="/chat" className="chat-icon-link" onClick={() => setMenuOpen(false)} aria-label="Chat" title="聊天訊息">
              <svg className="chat-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {unreadMessages > 0 && (
                <span className="chat-icon-badge">{unreadMessages > 99 ? '99+' : unreadMessages}</span>
              )}
            </Link>
          )}
          {user ? (
            <div className="user-menu">
              <Link to="/settings" className="settings-gear-link" onClick={() => setMenuOpen(false)} aria-label="Settings" title="帳號設定">
                <svg className="settings-gear-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16.1666 12.5C16.0557 12.7513 16.0226 13.0302 16.0716 13.3005C16.1206 13.5708 16.2495 13.8203 16.4416 14.0167L16.4916 14.0667C16.6465 14.2215 16.7695 14.4053 16.8533 14.6076C16.9372 14.8099 16.9804 15.0268 16.9804 15.2458C16.9804 15.4649 16.9372 15.6817 16.8533 15.8841C16.7695 16.0864 16.6465 16.2702 16.4916 16.425C16.3368 16.5799 16.153 16.7029 15.9507 16.7867C15.7484 16.8706 15.5315 16.9138 15.3125 16.9138C15.0935 16.9138 14.8766 16.8706 14.6743 16.7867C14.472 16.7029 14.2882 16.5799 14.1333 16.425L14.0833 16.375C13.887 16.1829 13.6374 16.054 13.3671 16.005C13.0968 15.956 12.818 15.9891 12.5666 16.1C12.3202 16.2056 12.1123 16.3832 11.9691 16.6103C11.8259 16.8374 11.7539 17.1042 11.7625 17.375V17.5C11.7625 17.942 11.587 18.3659 11.2744 18.6785C10.9618 18.9911 10.5379 19.1667 10.0958 19.1667C9.65381 19.1667 9.22989 18.9911 8.91733 18.6785C8.60477 18.3659 8.42917 17.942 8.42917 17.5V17.425C8.4321 17.1462 8.35005 16.8728 8.19397 16.6436C8.03789 16.4144 7.81584 16.2406 7.55833 16.1458C7.30692 16.035 7.02808 16.0019 6.75781 16.0509C6.48754 16.0999 6.23799 16.2288 6.04166 16.4208L5.99166 16.4708C5.83682 16.6257 5.65302 16.7488 5.45071 16.8326C5.2484 16.9164 5.03155 16.9597 4.8125 16.9597C4.59345 16.9597 4.3766 16.9164 4.17429 16.8326C3.97198 16.7488 3.78818 16.6257 3.63333 16.4708C3.47845 16.316 3.35541 16.1322 3.27158 15.9299C3.18775 15.7276 3.14455 15.5107 3.14455 15.2917C3.14455 15.0726 3.18775 14.8558 3.27158 14.6535C3.35541 14.4511 3.47845 14.2674 3.63333 14.1125L3.68333 14.0625C3.87539 13.8662 4.00434 13.6166 4.05333 13.3464C4.10232 13.0761 4.06923 12.7972 3.95833 12.5458C3.85274 12.2995 3.67516 12.0916 3.4481 11.9484C3.22104 11.8052 2.95421 11.7331 2.68333 11.7417H2.55833C2.11631 11.7417 1.69238 11.5661 1.37983 11.2535C1.06727 10.941 0.891663 10.517 0.891663 10.075C0.891663 9.633 1.06727 9.20907 1.37983 8.89651C1.69238 8.58395 2.11631 8.40835 2.55833 8.40835H2.63333C2.91209 8.41128 3.18554 8.32923 3.41472 8.17315C3.6439 8.01707 3.8177 7.79502 3.9125 7.53751C4.0234 7.2861 4.05648 7.00726 4.0075 6.73699C3.95851 6.46672 3.82956 6.21717 3.63749 6.02084L3.58749 5.97084C3.43261 5.816 3.30957 5.6322 3.22574 5.42989C3.14191 5.22758 3.09871 5.01073 3.09871 4.79168C3.09871 4.57263 3.14191 4.35578 3.22574 4.15347C3.30957 3.95116 3.43261 3.76736 3.58749 3.61251C3.74234 3.45763 3.92614 3.33459 4.12845 3.25076C4.33076 3.16693 4.54761 3.12373 4.76666 3.12373C4.98571 3.12373 5.20256 3.16693 5.40487 3.25076C5.60718 3.33459 5.79098 3.45763 5.94583 3.61251L5.99583 3.66251C6.19216 3.85458 6.44171 3.98352 6.71198 4.03251C6.98225 4.0815 7.26109 4.04842 7.5125 3.93751H7.55833C7.80474 3.83193 8.01267 3.65435 8.15585 3.42729C8.29902 3.20023 8.37107 2.9334 8.3625 2.66251V2.50001C8.3625 2.05799 8.5381 1.63406 8.85066 1.32151C9.16322 1.00895 9.58714 0.833344 10.0292 0.833344C10.4712 0.833344 10.8951 1.00895 11.2077 1.32151C11.5202 1.63406 11.6958 2.05799 11.6958 2.50001V2.57501C11.6873 2.8459 11.7593 3.11273 11.9025 3.33979C12.0457 3.56685 12.2536 3.74443 12.5 3.85001C12.7514 3.96091 13.0302 3.994 13.3005 3.94501C13.5708 3.89602 13.8203 3.76708 14.0167 3.57501L14.0667 3.52501C14.2215 3.37013 14.4053 3.24709 14.6076 3.16326C14.8099 3.07943 15.0268 3.03623 15.2458 3.03623C15.4649 3.03623 15.6817 3.07943 15.8841 3.16326C16.0864 3.24709 16.2702 3.37013 16.425 3.52501C16.5799 3.67986 16.7029 3.86366 16.7867 4.06597C16.8706 4.26828 16.9138 4.48513 16.9138 4.70418C16.9138 4.92323 16.8706 5.14008 16.7867 5.34239C16.7029 5.5447 16.5799 5.7285 16.425 5.88335L16.375 5.93335C16.1829 6.12968 16.054 6.37923 16.005 6.6495C15.956 6.91977 15.9891 7.19861 16.1 7.45001V7.50001C16.2056 7.74643 16.3832 7.95435 16.6103 8.09753C16.8374 8.24071 17.1042 8.31276 17.375 8.30418H17.5C17.942 8.30418 18.3659 8.47978 18.6785 8.79234C18.9911 9.10489 19.1667 9.52882 19.1667 9.97084C19.1667 10.4129 18.9911 10.8368 18.6785 11.1493C18.3659 11.4619 17.942 11.6375 17.5 11.6375H17.425C17.1541 11.6461 16.8873 11.7181 16.6602 11.8613C16.4332 12.0045 16.2556 12.2124 16.15 12.4583L16.1666 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <span className="user-name">{user.name}</span>
              <button className="btn btn-outline" onClick={handleLogout}>{t('nav.logout')}</button>
            </div>
          ) : (
            <Link to="/login" className="btn btn-primary" onClick={() => setMenuOpen(false)}>
              {t('nav.login')}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
